import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from '@/lib/api/notifications';
import { getCached, setCached, isFresh } from '@/lib/api/cache';
import type { Notification } from '@/types/notification';

export interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  refetch: () => Promise<void>;
}

export function useNotifications(): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchNotifications = useCallback(async () => {
    const cacheKey = 'notifications';
    try {
      // Stale-while-revalidate: serve cached data immediately if present
      const cached = getCached<Notification[]>(cacheKey);
      if (cached) setNotifications(cached);

      // Skip network fetch if data is still fresh
      if (isFresh(cacheKey)) {
        if (mountedRef.current) setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      const response = await getNotifications();
      if (!mountedRef.current) return;
      if (response.success && response.data) {
        const notifData = Array.isArray(response.data) ? response.data : [];
        setCached(cacheKey, notifData, 60_000);
        setNotifications(notifData);
      } else {
        if (!cached) setNotifications([]);
      }
    } catch {
      if (!mountedRef.current) return;
      setError('Failed to load notifications');
      const cached = getCached<Notification[]>(cacheKey);
      if (!cached) setNotifications([]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markRead = useCallback(async (id: string) => {
    await markNotificationRead(id);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
    );
  }, []);

  const markAllRead = useCallback(async () => {
    await markAllNotificationsRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  }, []);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return { notifications, unreadCount, loading, error, markRead, markAllRead, refetch: fetchNotifications };
}
